import { getSize, getColor } from '@lib/common';
import { Popover, Skeleton } from 'antd';
import styled from 'styled-components';
import { QuestionItemComponent } from './question-item.component';
import { SectionEmptyDropZone } from './section-empty-drop-zone.component';
import { SectionDropTarget } from './section-drop-target.component';
import { useSectionItem } from '../hooks';
import { useState, useMemo } from 'react';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

var NOOP_MOVE_QUESTION = function NOOP_MOVE_QUESTION(_dragIndex, _hoverIndex, _sectionId) {
  return undefined;
};

var SectionPanel = styled.div.withConfig({
  displayName: "section-itemcomponent__SectionPanel",
  componentId: "sc-nrpui4-0"
})(["border-radius:", ";padding:", ";border:1px solid ", ";cursor:", ";display:flex;flex-direction:column;background-color:", ";transition:border-color 0.2s ease,background-color 0.2s ease;&:hover{border:1px solid ", ";background-color:", ";cursor:pointer;}"], getSize('typo12'), getSize('padding12'), function (_ref) {
  var $isFocused = _ref.$isFocused;
  return $isFocused ? getColor('fillIntActive') : 'transparent';
}, function (props) {
  return props.$isSelected ? 'default' : 'pointer';
}, function (_ref2) {
  var $isFocused = _ref2.$isFocused;
  return $isFocused ? '#f0f7ff' : 'transparent';
}, function (_ref3) {
  var $isFocused = _ref3.$isFocused;
  return $isFocused ? getColor('fillIntActive') : 'lightgrey';
}, function (_ref4) {
  var $isFocused = _ref4.$isFocused;
  return $isFocused ? '#f0f7ff' : '#f9fafc';
});
var StyledSectionHeaderContainer = styled.div.withConfig({
  displayName: "section-itemcomponent__StyledSectionHeaderContainer",
  componentId: "sc-nrpui4-1"
})(["display:flex;align-items:flex-start;gap:", ";"], getSize('padding8'));
var StyledBadge = styled.div.withConfig({
  displayName: "section-itemcomponent__StyledBadge",
  componentId: "sc-nrpui4-2"
})(["display:inline-flex;align-items:center;justify-content:center;padding:2px 6px;font-size:", ";line-height:", ";font-weight:500;color:", ";background:#dbeafe;border-radius:", ";font-weight:700;text-transform:uppercase;flex-shrink:0;user-select:none;-webkit-user-select:none;-moz-user-select:none;-ms-user-select:none;-o-user-select:none;"], getSize('typo12'), getSize('typo16'), getColor('fillIntActive'), getSize('radiusS'));
var StyledTextContainer = styled.div.withConfig({
  displayName: "section-itemcomponent__StyledTextContainer",
  componentId: "sc-nrpui4-3"
})(["display:flex;flex-direction:column;overflow:hidden;flex:1;"]);
var StyledSectionTitle = styled.div.withConfig({
  displayName: "section-itemcomponent__StyledSectionTitle",
  componentId: "sc-nrpui4-4"
})(["font-weight:600;font-size:", ";color:", ";white-space:nowrap;overflow:hidden;text-overflow:ellipsis;line-height:1.4;"], getSize('typo14'), function (_ref5) {
  var $isSectionHovered = _ref5.$isSectionHovered;
  return $isSectionHovered ? getColor('fillIntActive') : getColor('contentTitle');
});
var StyledSectionDescription = styled.div.withConfig({
  displayName: "section-itemcomponent__StyledSectionDescription",
  componentId: "sc-nrpui4-5"
})(["font-size:", ";color:", ";white-space:nowrap;overflow:hidden;text-overflow:ellipsis;margin-top:", ";"], getSize('typo13'), getColor('textIntSub'), getSize('padding4'));
var QuestionsContainer = styled.div.withConfig({
  displayName: "section-itemcomponent__QuestionsContainer",
  componentId: "sc-nrpui4-6"
})(["margin-left:", ";padding-left:", ";position:relative;"], getSize('padding24'), getSize('padding12'));
var SectionPanelWrapper = styled.div.withConfig({
  displayName: "section-itemcomponent__SectionPanelWrapper",
  componentId: "sc-nrpui4-7"
})(["display:flex;flex-direction:column;gap:", ";"], getSize('padding4'));
var StyledPointTag = styled.span.withConfig({
  displayName: "section-itemcomponent__StyledPointTag",
  componentId: "sc-nrpui4-8"
})(["font-size:", ";font-weight:600;color:#94a2b8;background-color:#f1f5f9;padding:2px 6px;border-radius:", ";"], getSize('typo12'), getSize('radiusS'));
export var SectionItemComponent = function SectionItemComponent(_ref6) {
  var section = _ref6.section,
      sectionIndex = _ref6.sectionIndex,
      questions = _ref6.questions,
      selectedFormItemId = _ref6.selectedFormItemId,
      sectionFocusedId = _ref6.sectionFocusedId,
      hasUnsavedQuestionOrSectionChanges = _ref6.hasUnsavedQuestionOrSectionChanges,
      onSelectedSection = _ref6.onSelectedSection,
      onSelectQuestion = _ref6.onSelectQuestion,
      labelTranslation = _ref6.labelTranslation,
      sectionIdProp = _ref6.sectionId,
      onMoveQuestion = _ref6.onMoveQuestion,
      onMoveQuestionToSection = _ref6.onMoveQuestionToSection;

  var _useState = useState(false),
      isSectionHovered = _useState[0],
      setIsSectionHovered = _useState[1];

  var _useSectionItem = useSectionItem({
    section: section,
    questions: questions,
    selectedFormItemId: selectedFormItemId
  }),
      sectionId = _useSectionItem.sectionId,
      formattedTitle = _useSectionItem.formattedTitle,
      formattedDescription = _useSectionItem.formattedDescription,
      hasQuestions = _useSectionItem.hasQuestions,
      sectionPoints = _useSectionItem.sectionPoints,
      isLoading = _useSectionItem.isLoading,
      convertToRomanNumeral = _useSectionItem.convertToRomanNumeral;

  var resolvedSectionId = sectionIdProp || sectionId;
  var isFocused = sectionId === sectionFocusedId;
  var hasDescription = useMemo(function () {
    if (!section.description) return false;
    var trimmed = section.description.trim();

    if (!trimmed || trimmed === '' || trimmed === '<p><br></p>' || trimmed === '<p></p>' || trimmed === '<br>' || trimmed === 'Instructional text') {
      return false;
    }

    if (!formattedDescription) return false;
    var formattedTrimmed = formattedDescription.trim();

    if (!formattedTrimmed || formattedTrimmed === '' || formattedTrimmed === 'Instructional text') {
      return false;
    }

    return true;
  }, [section.description, formattedDescription]);

  var renderPointTag = function renderPointTag() {
    if (isLoading || sectionPoints === null || sectionPoints === undefined) {
      return /*#__PURE__*/_jsx(Skeleton.Button, {
        active: true,
        size: "small"
      });
    }

    var displayPoints = sectionPoints === 0 ? '0' : sectionPoints;
    return /*#__PURE__*/_jsxs(StyledPointTag, {
      children: [displayPoints, sectionPoints > 1 ? labelTranslation.pointsShort : labelTranslation.pointShort]
    });
  };

  var sectionInner = /*#__PURE__*/_jsxs(SectionPanelWrapper, {
    children: [/*#__PURE__*/_jsx(SectionPanel, {
      $isFocused: isFocused,
      onClick: function onClick() {
        return onSelectedSection(sectionId);
      },
      onMouseEnter: function onMouseEnter() {
        return setIsSectionHovered(true);
      },
      onMouseLeave: function onMouseLeave() {
        return setIsSectionHovered(false);
      },
      children: /*#__PURE__*/_jsxs(StyledSectionHeaderContainer, {
        children: [/*#__PURE__*/_jsx(StyledBadge, {
          children: convertToRomanNumeral(sectionIndex + 1)
        }), /*#__PURE__*/_jsxs(StyledTextContainer, {
          children: [/*#__PURE__*/_jsx(StyledSectionTitle, {
            $isSectionHovered: isSectionHovered,
            children: formattedTitle
          }), hasDescription && /*#__PURE__*/_jsx(StyledSectionDescription, {
            children: formattedDescription
          })]
        }), renderPointTag()]
      })
    }), hasQuestions ? /*#__PURE__*/_jsx(QuestionsContainer, {
      children: /*#__PURE__*/_jsx(QuestionItemComponent, {
        questions: questions,
        hasUnsavedQuestionOrSectionChanges: hasUnsavedQuestionOrSectionChanges,
        onSelectQuestion: onSelectQuestion,
        labelTranslation: labelTranslation,
        selectedFormItemId: selectedFormItemId,
        sectionId: resolvedSectionId,
        onMoveQuestion: onMoveQuestion !== null && onMoveQuestion !== void 0 ? onMoveQuestion : NOOP_MOVE_QUESTION,
        onMoveQuestionToSection: onMoveQuestionToSection
      })
    }) : onMoveQuestionToSection && /*#__PURE__*/_jsx(SectionEmptyDropZone, {
      sectionId: resolvedSectionId,
      disabled: Boolean(hasUnsavedQuestionOrSectionChanges),
      onDropQuestion: onMoveQuestionToSection
    })]
  });

  var sectionBody = onMoveQuestionToSection && !hasUnsavedQuestionOrSectionChanges ? /*#__PURE__*/_jsx(SectionDropTarget, {
    sectionId: resolvedSectionId,
    appendIndex: (questions === null || questions === void 0 ? void 0 : questions.length) || 0,
    onDropQuestion: onMoveQuestionToSection,
    children: sectionInner
  }) : sectionInner;
  return /*#__PURE__*/_jsx("div", {
    id: sectionId + '-panel',
    children: !hasUnsavedQuestionOrSectionChanges ? sectionBody : /*#__PURE__*/_jsx(Popover, {
      title: "",
      trigger: 'hover',
      children: sectionBody
    })
  });
};